import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { useHiveRevision } from '@gitbook/hive-react';
import type { Document } from '@gitbook/slate';
import { Popover, PopoverRoot, PopoverTrigger, Skeleton } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useNavigationState } from '../../../navigation';
import { apiQuery } from '../../../state';
import {
    CommentInput,
    type CommentTarget,
    CommentTargetPreview,
    getSpaceIdFromScreen,
    UserAvatarSkeleton,
    useCurrentHivePage,
    useDocumentNodeComments,
} from '../..';
import { PopoverContainer } from './PopoverContainer';

export interface AddCommentPopoverProps {
    /** The node key commented on. If undefined, comment is on the entire page */
    nodeKey: CommentTarget;
    /** Table cell details if the comment is on a table cell */
    tableCell?: api.TableCellTarget;
    /** Popover placement */
    placement?: 'right-start' | 'bottom-start' | 'bottom' | 'top' | 'left' | 'right';
    /** Trigger element */
    trigger: React.ReactNode;
}

/**
 * Smart component that manages comment creation with popover state.
 */
export function AddCommentPopover(props: AddCommentPopoverProps) {
    const { nodeKey, tableCell, placement = 'right-start', trigger } = props;

    // Get the (pending) comment state and handlers
    const { nodeState, submitComment, closePopover } = useDocumentNodeComments({
        nodeKey,
        tableCell,
    });

    return (
        <PopoverRoot
            isOpen={nodeState.addNewPopoverIsOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    closePopover();
                }
            }}
        >
            <PopoverTrigger>{trigger}</PopoverTrigger>
            <Popover placement={placement} sticky>
                <React.Suspense fallback={<AddCommentPopoverSkeleton />}>
                    {nodeState.addNewPopoverIsOpen ? (
                        <AddCommentPopoverContent
                            nodeKey={nodeKey}
                            tableCell={tableCell}
                            submitComment={submitComment}
                            closePopover={closePopover}
                        />
                    ) : null}
                </React.Suspense>
            </Popover>
        </PopoverRoot>
    );
}

function AddCommentPopoverContent(props: {
    nodeKey: CommentTarget;
    tableCell?: api.TableCellTarget;
    submitComment: (body: Document) => Promise<void>;
    closePopover: () => void;
}) {
    const { nodeKey, tableCell, submitComment, closePopover } = props;

    // Navigation and page context are only needed when the popover is shown.
    const { screen } = useNavigationState();
    const spaceId = getSpaceIdFromScreen(screen);

    const { data: space } = useSuspenseQuery(
        apiQuery('getSpaceById', spaceId ? { spaceId } : skipToken)
    );
    assertIsDefined(space, 'Space should be defined in a `space` or `change_requests` screen');

    const { revision } = useHiveRevision();
    const [currentPage] = useCurrentHivePage(revision, screen.type === 'space' ? screen.page : '');

    return (
        <PopoverContainer>
            <div className="p-2">
                {currentPage?.id && space.id && (
                    <CommentTargetPreview
                        pageId={currentPage.id}
                        spaceId={space.id}
                        tableCell={tableCell}
                        nodeKey={nodeKey}
                    />
                )}
            </div>
            <CommentInput
                testID="comment.postFirstNodeComment"
                placeholder="Leave a comment..."
                targetNode={nodeKey}
                onSubmit={submitComment}
                onCancel={closePopover}
            />
        </PopoverContainer>
    );
}

function AddCommentPopoverSkeleton() {
    return (
        <div className="flex h-fit min-w-76 max-w-76 flex-col gap-2 overflow-hidden rounded-lg border border-base bg-base p-2 shadow-subtle">
            {/* Preview area */}
            <div className="flex flex-row gap-2">
                <Skeleton className="h-5 w-0.5 rounded-full" />
                <Skeleton className="h-5 w-3/4" />
            </div>

            {/* Comment input area */}
            <div className="flex items-center gap-2">
                <UserAvatarSkeleton size="sm" />
                <Skeleton className="h-9 flex-1 rounded-lg" />
            </div>
            {/* Comment actions */}
            <div className="ml-8 flex flex-1 items-center justify-between gap-2">
                <div className="flex gap-1">
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-6 w-6" />
                </div>
                <Skeleton className="h-6 w-16" />
            </div>
        </div>
    );
}
